import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../../../store';
import { loadNodes, testNode, pingTestNode } from './Interfaces.api';
import { getCollectionConfig } from '../Interfaces.config';

const useInterfaceNodes = (moduleId, collectionId) => {
  const dispatch = useDispatch();
  const [nodes, setNodes] = useState([]);
  const [dialogState, setDialogState] = useState({ open: false, editingNode: null });

  const collection = getCollectionConfig(moduleId, collectionId);

  const loadCollectionNodes = useCallback(async () => {
    if (!collection) return;

    const results = await Promise.all(
      collection.typeIds.map(async (typeId) => ({
        typeId,
        response: await loadNodes(typeId),
      }))
    );

    const failures = results.filter(({ response }) => !response.success);
    const loadedNodes = results.flatMap(({ typeId, response }) =>
      response.success ? (response.data || []).map((node) => ({ ...node, _typeId: typeId })) : []
    );

    setNodes(loadedNodes);

    if (failures.length > 0) {
      dispatch(
        actions.setSnackbar({
          msg: `api_messages.${failures[0].response.msg}`,
          severity: 'error',
        })
      );
    }
  }, [collection, dispatch]);

  useEffect(() => {
    setNodes([]);
  }, [moduleId, collectionId]);

  useEffect(() => {
    loadCollectionNodes();
  }, [loadCollectionNodes]);

  const openDialog = (node = null) => {
    setDialogState({ open: true, editingNode: node });
  };

  const closeDialog = () => {
    setDialogState({ open: false, editingNode: null });
  };

  const handleSaved = async () => {
    await loadCollectionNodes();
    closeDialog();
  };

  const handleTest = useCallback(async (typeId, nodeName) => {
    return await testNode(typeId, nodeName);
  }, []);

  const handlePingTest = useCallback(async (typeId, nodeName) => {
    return await pingTestNode(typeId, nodeName);
  }, []);

  return {
    nodes,
    dialogState,
    openDialog,
    closeDialog,
    handleSaved,
    loadCollectionNodes,
    handleTest,
    handlePingTest,
  };
};

export default useInterfaceNodes;
